import {
  AGGREGATION_GRADE,
  AGGREGATION_STUDENT,
  AGGREGATION_TEACHER,
  BASE_ENTITY_LABEL,
  ResultRow,
  SelectedGradeOption,
  SelectedTeacherOption,
  buildGradePlaceholders,
  buildPlaceholderRow,
  buildTeacherPlaceholders,
  makeGetSortableEntityName,
  supplementRowsWithPlaceholders,
} from '../../adminMomentumReports/helpers'

// Shared logic is re-exported so consumers and tests have one import point per report.
export * from '../../adminMomentumReports/helpers'

export const AGGREGATION_SKILL = 'skill'

export const groupByDropdownOptions = [
  { label: 'Skill', value: AGGREGATION_SKILL },
  { label: 'Grade', value: AGGREGATION_GRADE },
  { label: 'Teacher', value: AGGREGATION_TEACHER },
  { label: 'Student', value: AGGREGATION_STUDENT },
]

export const ENTITY_LABEL = {
  [AGGREGATION_SKILL]: { singular: 'skill category', plural: 'skill categories' },
  ...BASE_ENTITY_LABEL,
}

export interface SkillConcept {
  id: number;
  name: string;
  ell: boolean;
  gen_ed: boolean;
}

export interface SubConcept extends SkillConcept {
  parent_id: number;
}

export const ALL_SUB_CATEGORIES_OPTION = { label: 'All sub-categories', value: 'all' }

export const ADMIN_SKILLS_REPORT_MOMENTUM_SCORE_GOAL = 'adminSkillsReportMomentumScoreGoal'

export const SHOW_MORE_THRESHOLD = 4

// These grade values exist in the database, but are considered non-relevant to this report
// counted in the Smart Groups footer ("X grades haven't practiced this skill yet").
const NON_STANDARD_GRADE_NAMES = new Set([
  'No grade selected',
  'University',
  'Other',
])

export const isStandardGrade = (name: string) => !NON_STANDARD_GRADE_NAMES.has(name)

export const getSortableEntityName = makeGetSortableEntityName(AGGREGATION_SKILL)

const SKILL_SUB_CATEGORY_LABEL = { singular: 'skill sub-category', plural: 'skill sub-categories' }

export const getUnpracticedEntityLabel = (aggregation: string, conceptId: number | null) => {
  if (aggregation === AGGREGATION_SKILL && conceptId) return SKILL_SUB_CATEGORY_LABEL
  return ENTITY_LABEL[aggregation]
}

interface SupplementMissingRowsOpts {
  rows: ResultRow[];
  aggregation: string;
  conceptId: number | null;
  allConcepts: SkillConcept[];
  allSubConcepts: SubConcept[];
  ell: boolean;
  selectedGrades?: SelectedGradeOption[] | null;
  selectedTeachers?: SelectedTeacherOption[] | null;
}

const buildSkillPlaceholders = ({ rows, conceptId, allConcepts, allSubConcepts, ell }: SupplementMissingRowsOpts): ResultRow[] => {
  const expected = conceptId
    ? allSubConcepts.filter((c) => c.parent_id === conceptId && (ell ? c.ell : c.gen_ed))
    : allConcepts.filter((c) => (ell ? c.ell : c.gen_ed))

  const presentIds = new Set(rows.map((row) => Number(row.aggregate_id)))
  return expected
    .filter((c) => !presentIds.has(c.id))
    .map((c) => buildPlaceholderRow(c.id, c.name, AGGREGATION_SKILL))
}

const PLACEHOLDER_BUILDERS: Record<string, (opts: SupplementMissingRowsOpts) => ResultRow[]> = {
  [AGGREGATION_SKILL]: buildSkillPlaceholders,
  [AGGREGATION_GRADE]: buildGradePlaceholders,
  [AGGREGATION_TEACHER]: buildTeacherPlaceholders,
}

// Student aggregation has no fixed universe to supplement with placeholders.
export const supplementMissingRows = (opts: SupplementMissingRowsOpts): ResultRow[] => supplementRowsWithPlaceholders(opts, PLACEHOLDER_BUILDERS)
